import { z } from 'zod';
import { PinComponentSchema, LayoutPartSchema } from '../pin-component';
import { AnswerCardPinThemeSettingsSchema } from '../setting';
import { BaseInteractiveQuestionPinSettingsSchema, BasePinSchema, BaseTemplateLayoutSchema } from './base-pin';
import { BaseTeacherPinSettingsSchema } from './base-teacher-pin';

const OpenQuestionPinLayoutKeySettingsSchema = z.enum([
  'TITLE_ONLY',
  'IMAGE_LEFT',
  'IMAGE_RIGHT',
  'IMAGE_ONLY',
  'EMPTY',
  'FIFTY_FIFTY', // TODO Remove and replace by 'IMAGE_LEFT'
]);

// TODO Remove all the following and replace by 2.0 layout keys - once lesson conversion is done
const LegacyOpenQuestionPinLayoutKeySettingsSchema = z.enum([
  'LEGACY_TITLE_ONLY',
  'LEGACY_QUESTION_LEFT_WITH_IMAGE',
  'LEGACY_QUESTION_RIGHT_WITH_IMAGE',
  'LEGACY_IMAGE_ONLY',
  'LEGACY_EMPTY',
]);

const CombinedOpenQuestionKeySettingsSchema = z.union([
  OpenQuestionPinLayoutKeySettingsSchema,
  LegacyOpenQuestionPinLayoutKeySettingsSchema,
]);
export type OpenQuestionPinLayoutKey = z.infer<typeof CombinedOpenQuestionKeySettingsSchema>;

// TODO: Workaround number enum https://github.com/colinhacks/zod/issues/2686#issuecomment-1906248858
// Numbers are from v1, not defined for v2. Photo questions might be out of scope.
export enum NUMBER_OF_IMAGES_ALLOWED {
  zero = 0,
  one = 1,
  five = 5,
}

const OpenQuestionLayoutPartKeySchema = LayoutPartSchema.extract(['TITLE', 'IMAGE1', 'ANSWER_AREA']);

export const OpenQuestionPinSettingsSchema = BaseInteractiveQuestionPinSettingsSchema.extend(
  z.object({
    explanation: z.string().nullable(),
    layout: CombinedOpenQuestionKeySettingsSchema,
    caseSensitive: z.boolean(),
    diacriticSensitive: z.boolean(),
    maximumNumberOfUploadsPerStudent: z.enum(NUMBER_OF_IMAGES_ALLOWED),
    showStudentNames: z.boolean(),
    layoutParts: z.partialRecord(OpenQuestionLayoutPartKeySchema, z.string().optional()),
    answerCard: AnswerCardPinThemeSettingsSchema.optional(),
  }).shape
);

export type OpenQuestionPinSettings = z.infer<typeof OpenQuestionPinSettingsSchema>;

export const OpenQuestionPinLayoutTemplateSchema = BaseTemplateLayoutSchema.extend(
  z.object({
    key: CombinedOpenQuestionKeySettingsSchema,
    layoutParts: z.partialRecord(OpenQuestionLayoutPartKeySchema, PinComponentSchema.optional()),
  }).shape
);
export type OpenQuestionPinLayoutTemplate = z.infer<typeof OpenQuestionPinLayoutTemplateSchema>;

export const OpenQuestionPinTypeSchema = z.literal('OPEN_QUESTION');

export const OpenQuestionPinSchema = BasePinSchema.extend(
  z.object({
    type: OpenQuestionPinTypeSchema,
    settings: OpenQuestionPinSettingsSchema,
  }).shape
);

export const OpenQuestionTeacherPinSettingsSchema = BaseTeacherPinSettingsSchema.extend(
  z.object({
    correctAnswers: z.array(z.string()).optional(),
    manuallyApprovedAnswers: z.array(z.string()).optional(),
    manuallyRejectedAnswers: z.array(z.string()).optional(),
  }).shape
);

export type OpenQuestionTeacherPinSettings = z.infer<typeof OpenQuestionTeacherPinSettingsSchema>;

export type OpenQuestionPin = z.infer<typeof OpenQuestionPinSchema>;
